import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { createMoneyFormatter, type MoneyPresentation } from "@facturar/money-format";
import { useAuth } from "@/context/AuthContext";
import { apiRequest } from "./api";

export const MoneyPresentationContext = createContext<MoneyPresentation>({});

/** Same authenticated user preference as General Settings in the portal. */
export function MoneyPresentationProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const { data } = useQuery({
    queryKey: ["admin", "money-presentation", auth.user?.id ?? null],
    enabled: auth.status === "authenticated",
    queryFn: () => apiRequest<{ profile: MoneyPresentation | null }>("/api/me/profile"),
    staleTime: 10_000,
    refetchOnWindowFocus: true,
  });
  const locale = data?.profile?.locale;
  const digits = data?.profile?.monetaryDecimalPlaces;
  const value = useMemo(() => ({ locale, monetaryDecimalPlaces: digits }), [locale, digits]);
  return <MoneyPresentationContext.Provider value={auth.status === "authenticated" ? value : {}}>{children}</MoneyPresentationContext.Provider>;
}

export function useMoneyFormatter() {
  const preferences = useContext(MoneyPresentationContext);
  return useMemo(() => createMoneyFormatter(preferences), [preferences]);
}
